import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import CyberButton from '../components/ui/CyberButton';
import TechnicalAnnotation from '../components/scene/TechnicalAnnotation';
import { useChapterScene } from '../context/SceneContext';
import { ShieldSchematic, NeuralMesh, MechanicalAssembly } from '../components/three/Schematics';
import { StatusPill, TelemetryRow } from '../components/scene/Telemetry';
import AnimatedNumber from '../components/ui/AnimatedNumber';
import TechMarquee from '../components/scene/TechMarquee';
import BriefingStrip from '../components/scene/BriefingStrip';

const LINKEDIN_URL = 'https://www.linkedin.com/in/kingsley-okafor-9b150127b/';

const stats = [
  { label: 'Products Shipped',   value: '50+'   },
  { label: 'Users Served',       value: '1M+'   },
  { label: 'Uptime',             value: '99.7%' },
  { label: 'Years Experience',   value: '7+'    },
];

const PROTOCOLS = [
  {
    id: '01',
    name: 'Zero Trust\nDefense',
    body: 'Securing cloud environments where a single breach resets everything.',
    tags: ['Cloud', 'Azure', 'ZTA', 'SIEM'],
    Schematic: ShieldSchematic,
    telemetry: { latency: 'sub-40ms', exposure: '−31%' },
  },
  {
    id: '02',
    name: 'Intelligence\nLayer',
    body: 'ML pipeline converting 50K+ IoT signals into closed-loop decisions. Sub-120ms.',
    tags: ['AI / ML', 'IoT', 'Power BI', 'Edge'],
    Schematic: NeuralMesh,
    telemetry: { latency: 'sub-120ms', throughput: '50k/sec' },
  },
  {
    id: '03',
    name: 'Precision\nEngineering',
    body: 'Parametric CAD to production. Shipped to spec, shipped on time.',
    tags: ['SolidWorks', 'Python', 'FDM', 'Arduino'],
    Schematic: MechanicalAssembly,
    telemetry: { cycle: '3-week', dfm: '−22%' },
  },
];

const BlurReveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 22, filter: 'blur(8px)' }}
    whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function HomePage() {
  const heroRef = useRef(null);
  const heroSceneRef = useChapterScene('command-center');
  const protocolsSceneRef = useChapterScene('protocols');
  const ctaSceneRef = useChapterScene('signal-hub');
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '-10%']);
  const robotY = useTransform(scrollYProgress, [0, 1], ['0%', '6%']);
  const heroOp = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <div className="relative" data-testid="home-page">

      {/* ══════════════════════════════════════════════
          HERO — lean, high-conversion
      ══════════════════════════════════════════════ */}
      <section
        ref={(el) => { heroRef.current = el; heroSceneRef.current = el; }}
        className="relative min-h-screen w-full overflow-y-hidden"
        data-testid="hero-section"
      >
        <motion.div
          className="relative z-10 min-h-screen max-w-7xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28"
          style={{ y: textY }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center min-h-[calc(100vh-180px)]">

            {/* Left — copy */}
            <div className="space-y-6 relative z-20">
              <BlurReveal delay={0.05}>
                <div className="font-mono text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-[var(--hyper-cyan)]">
                  KINGSLEY.O · PRODUCT ARCHITECT · CLOUD SECURITY · AI/ML
                </div>
              </BlurReveal>

              <BlurReveal delay={0.3}>
                <p className="font-rajdhani text-base sm:text-lg text-white/50 max-w-md leading-relaxed">
                  Cloud security, AI / ML, precision engineering — the three systems that decide whether modern tech survives.
                </p>
              </BlurReveal>

              <BlurReveal delay={0.42}>
                <div className="flex flex-wrap gap-3">
                  <Link to="/projects">
                    <CyberButton variant="accent" size="md" data-testid="view-work-btn">
                      View Deployments <ArrowRight size={15} />
                    </CyberButton>
                  </Link>
                  <CyberButton
                    variant="secondary" size="md"
                    href={LINKEDIN_URL}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="linkedin-btn"
                  >
                    <Linkedin size={15} /> LinkedIn
                  </CyberButton>
                </div>
              </BlurReveal>
            </div>

            {/* Right — 3D Robot */}
            <motion.div
              className="relative h-[340px] sm:h-[420px] lg:h-[540px] xl:h-[620px] overflow-visible"
              style={{ y: robotY, opacity: heroOp }}
            >
              <div className="absolute inset-0 overflow-visible">
                <iframe
                  src="https://my.spline.design/genkubgreetingrobot-QCUpKxwYSg8L4MMzfeKlKBXZ/"
                  frameBorder="0" title="3D Robot" loading="lazy"
                  style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', border:'none', display:'block' }}
                />
                <div style={{ position:'absolute', bottom:0, right:0, width:'220px', height:'60px', background:'#0E1116', zIndex:10, pointerEvents:'none' }} />
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Tighter stats bar */}
        <div className="absolute bottom-0 left-0 right-0 z-20 border-t border-white/8 bg-black/30 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-8">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  className="flex items-baseline gap-2 sm:justify-center"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.8 + i * 0.08 }}
                >
                  <span className="font-headline text-xl sm:text-2xl font-black text-[var(--hyper-cyan)] tracking-tight" data-testid={`stat-value-${i}`}>
                    <AnimatedNumber value={s.value} duration={1500 + i * 120} />
                  </span>
                  <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/40">{s.label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          BRIEFING — 4s recruiter elevator pitch
      ══════════════════════════════════════════════ */}
      <BriefingStrip />

      {/* ══════════════════════════════════════════════
          TRUST BAND — infinite tech marquee
      ══════════════════════════════════════════════ */}
      <TechMarquee />

      {/* ══════════════════════════════════════════════
          THREE PROTOCOLS — the core of the pitch
      ══════════════════════════════════════════════ */}
      <section ref={protocolsSceneRef} className="relative border-t border-white/5" data-testid="protocols-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">

          <BlurReveal>
            <div className="mb-10 sm:mb-14">
              <TechnicalAnnotation code="CH · 01 / THREE PROTOCOLS" title="Modules deployed in parallel" />
            </div>
          </BlurReveal>

          <BlurReveal delay={0.05}>
            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tighter mb-12 sm:mb-16 leading-[0.98]">
              Three <span className="gradient-text">Protocols.</span>
              <span className="block font-rajdhani text-base sm:text-lg normal-case tracking-normal text-white/38 mt-3 font-normal">
                Each mastered independently. All three deployed simultaneously.
              </span>
            </h2>
          </BlurReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 border border-white/10 md:auto-rows-fr bg-black/25 backdrop-blur-[2px]">
            {PROTOCOLS.map((p, i) => (
              <BlurReveal
                key={p.id}
                delay={0.1 + i * 0.1}
                className={[
                  'border-b border-white/10 flex',
                  i < 2 ? 'md:border-r md:border-white/10 md:border-b-0' : 'border-b-0',
                ].join(' ')}
              >
                <div className="p-5 sm:p-6 w-full flex flex-col gap-3.5 group hover:bg-white/[0.025] transition-colors duration-500">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-[9px] tracking-[0.5em] text-[var(--hyper-cyan)] uppercase">
                      PROTOCOL / {p.id}
                    </div>
                    <StatusPill status="active" label="active" />
                  </div>

                  <div className="relative -mx-2 sm:-mx-3">
                    <p.Schematic height={130} />
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 font-mono text-[8px] tracking-[0.28em] uppercase text-white/28">
                      SCHEMATIC / {p.id}
                    </div>
                  </div>

                  <h3 className="font-headline text-xl sm:text-2xl font-black uppercase leading-[1.02] whitespace-pre-line tracking-tight">
                    {p.name}
                  </h3>
                  <p className="font-rajdhani text-white/48 leading-snug text-[15px]">
                    {p.body}
                  </p>

                  <div className="flex-1 min-h-0" aria-hidden="true" />

                  <div className="space-y-1 border-t border-white/10 pt-3">
                    {Object.entries(p.telemetry).map(([k, v]) => (
                      <TelemetryRow
                        key={k}
                        label={k}
                        value={v}
                        accent={k === 'latency' || k === 'exposure' || k === 'dfm'}
                      />
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {p.tags.map(t => (
                      <span key={t} className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/40 border border-white/10 px-1.5 py-0.5">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </BlurReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          CTA — signal hub. Lean, conversion-focused.
      ══════════════════════════════════════════════ */}
      <section ref={ctaSceneRef} className="relative border-t border-white/5" data-testid="cta-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
          <BlurReveal>
            <div className="font-mono text-[10px] sm:text-[11px] tracking-[0.4em] uppercase text-[var(--hyper-cyan)] mb-5">
              — OPEN THE CHANNEL —
            </div>
          </BlurReveal>
          <BlurReveal delay={0.1}>
            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tighter leading-[0.98] mb-5">
              The architecture is<br />
              <span className="gradient-text">ready.</span>
            </h2>
          </BlurReveal>
          <BlurReveal delay={0.2}>
            <p className="font-rajdhani text-base sm:text-lg text-white/48 max-w-md mx-auto mb-8">
              If you're building something that has to survive contact with reality, let's design the system together.
            </p>
          </BlurReveal>
          <BlurReveal delay={0.3}>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/contact">
                <CyberButton variant="accent" size="md" data-testid="home-contact-btn">
                  Let's Talk <ArrowRight size={15} />
                </CyberButton>
              </Link>
              <CyberButton
                variant="secondary" size="md"
                href={LINKEDIN_URL}
                target="_blank" rel="noopener noreferrer"
                data-testid="home-linkedin-btn"
              >
                <Linkedin size={15} /> LinkedIn
              </CyberButton>
            </div>
          </BlurReveal>
        </div>
      </section>
    </div>
  );
}
